// Zip archives, read and written in the browser: projects are imported from
// and downloaded as zips, and web builds are downloaded as one. Entries are
// stored or deflated (method 8), with the browser's own deflate.

const CRC_TABLE = (() => {
  const table = new Uint32Array(256);
  for (let n = 0; n < 256; ++n) {
    let c = n;
    for (let k = 0; k < 8; ++k) c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
    table[n] = c >>> 0;
  }
  return table;
})();

function crc32(bytes) {
  let crc = 0xffffffff;
  for (let k = 0; k < bytes.length; ++k) crc = CRC_TABLE[(crc ^ bytes[k]) & 0xff] ^ (crc >>> 8);
  return (crc ^ 0xffffffff) >>> 0;
}

const pipe = async (bytes, stream) =>
  new Uint8Array(await new Response(new Blob([bytes]).stream().pipeThrough(stream)).arrayBuffer());

// Media that is already compressed is stored as it is.
const STORED = /\.(png|jpe?g|gif|webp|ogg|mp3|m4a|glb|zip|wasm|b3dpak|basis|ktx2)$/i;

// Returns a Blob holding a zip of entries: [[path, Uint8Array or string], ...].
export async function writeZip(entries) {
  const encoder = new TextEncoder();
  const parts = [], central = [];
  let offset = 0;
  // DOS time: the archive does not record when files changed.
  const time = 0, date = (1 << 5) | 1;
  for (const [path, contents] of entries) {
    const name = encoder.encode(path);
    const data = typeof contents === "string" ? encoder.encode(contents) : contents;
    const crc = crc32(data);
    let method = 0, stored = data;
    if (!STORED.test(path) && data.length > 64) {
      const deflated = await pipe(data, new CompressionStream("deflate-raw"));
      if (deflated.length < data.length) {
        method = 8;
        stored = deflated;
      }
    }
    const header = new DataView(new ArrayBuffer(30));
    header.setUint32(0, 0x04034b50, true);
    header.setUint16(4, 20, true);
    header.setUint16(6, 0x0800, true);          // names are UTF-8
    header.setUint16(8, method, true);
    header.setUint16(10, time, true);
    header.setUint16(12, date, true);
    header.setUint32(14, crc, true);
    header.setUint32(18, stored.length, true);
    header.setUint32(22, data.length, true);
    header.setUint16(26, name.length, true);
    parts.push(header.buffer, name, stored);
    const entry = new DataView(new ArrayBuffer(46));
    entry.setUint32(0, 0x02014b50, true);
    entry.setUint16(4, 20, true);
    entry.setUint16(6, 20, true);
    entry.setUint16(8, 0x0800, true);
    entry.setUint16(10, method, true);
    entry.setUint16(12, time, true);
    entry.setUint16(14, date, true);
    entry.setUint32(16, crc, true);
    entry.setUint32(20, stored.length, true);
    entry.setUint32(24, data.length, true);
    entry.setUint16(28, name.length, true);
    entry.setUint32(42, offset, true);
    central.push(entry.buffer, name);
    offset += 30 + name.length + stored.length;
  }
  const centralSize = central.reduce((total, part) => total + part.byteLength, 0);
  const end = new DataView(new ArrayBuffer(22));
  end.setUint32(0, 0x06054b50, true);
  end.setUint16(8, entries.length, true);
  end.setUint16(10, entries.length, true);
  end.setUint32(12, centralSize, true);
  end.setUint32(16, offset, true);
  return new Blob([...parts, ...central, end.buffer], { type: "application/zip" });
}

// Reads a zip: returns [[path, Uint8Array], ...] for its files, leaving out
// directories. Throws on archives it cannot read (Zip64, encryption, or
// methods other than stored and deflated).
export async function readZip(buffer) {
  const bytes = new Uint8Array(buffer);
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  let end = -1;
  for (let k = bytes.length - 22; k >= Math.max(0, bytes.length - 65557); --k) {
    if (view.getUint32(k, true) === 0x06054b50) {
      end = k;
      break;
    }
  }
  if (end < 0) throw new Error("This is not a zip file");
  const count = view.getUint16(end + 10, true);
  let position = view.getUint32(end + 16, true);
  const utf8 = new TextDecoder(), legacy = new TextDecoder("latin1");
  const files = [];
  for (let index = 0; index < count; ++index) {
    if (view.getUint32(position, true) !== 0x02014b50) throw new Error("The zip file is damaged");
    const flags = view.getUint16(position + 8, true);
    const method = view.getUint16(position + 10, true);
    const size = view.getUint32(position + 20, true);
    const nameLength = view.getUint16(position + 28, true);
    const extraLength = view.getUint16(position + 30, true);
    const commentLength = view.getUint16(position + 32, true);
    const local = view.getUint32(position + 42, true);
    const nameBytes = bytes.subarray(position + 46, position + 46 + nameLength);
    const name = (flags & 0x0800 ? utf8 : legacy).decode(nameBytes).replace(/\\/g, "/");
    position += 46 + nameLength + extraLength + commentLength;
    if (name.endsWith("/")) continue;
    if (flags & 1) throw new Error(`${name} is encrypted`);
    if (size === 0xffffffff || local === 0xffffffff) throw new Error("Zip64 archives are not supported");
    const dataStart = local + 30 + view.getUint16(local + 26, true) + view.getUint16(local + 28, true);
    const data = bytes.subarray(dataStart, dataStart + size);
    if (method === 0) files.push([name, data.slice()]);
    else if (method === 8) files.push([name, await pipe(data, new DecompressionStream("deflate-raw"))]);
    else throw new Error(`${name} is compressed in a way this page cannot read`);
  }
  return files;
}
